import { Button, Input, Select, Spin } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { FaSearch } from "react-icons/fa";
import { FaPlus } from "react-icons/fa6";
import { FiEdit, FiEye, FiTrash, FiUserCheck, FiUserX } from "react-icons/fi";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { api, endpoint } from "@/api";
import appConfig from "@/config/appConfig";
import { DataTable } from "@/components";
import { getFormatedUserType } from "@/utils/userTypeFomater";
import { useSearchQuery } from "@/utils/useSearchQuery";
import { flare } from "@flareapp/js";

const getHeader = (role) => {
  switch (role) {
    case "installer":
      return {
        title: "All Installers",
        btnText: "Add Installer",
        btnLink: "/users/add-public-user",
        role: "Installer",
      };
    case "admin":
      return {
        title: "All Site Admins",
        btnText: "Add Site Admin",
        btnLink: "/users/add-public-user",
        role: "Site Admin",
      };
    case "user":
      return {
        title: "All Site Users",
        btnText: "Add Site User",
        btnLink: "/users/add-public-user",
        role: "User",
      };
    case "public":
      return {
        title: "All Public Users",
        btnText: "Add Public User",
        btnLink: "/users/add-public-user",
        role: "Public User",
      };
    case "businessAdmin":
      return {
        title: "All Business Admin",
        btnText: "Add Business Admin",
        btnLink: "/users/add-Business-Admin",
        role: "Business Admin",
      };
    default:
      return {
        title: "All Users",
        btnText: "Add Site Public User",
        btnLink: "/users/add-public-user",
        role: "User",
      };
  }
};

const UserList = () => {
  const role = useSearchQuery("role");
  // const { business } = useSelector((state) => state?.user);

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [businessLoading, setBusinessLoading] = useState(false);
  const [businessList, setBusinessList] = useState([]);
  const [siteLoading, setSiteLoading] = useState(false);
  const [siteList, setSiteList] = useState([]);
  const [search, setSearch] = useState({});
  const [searchText, setSearchText] = useState("");

  const { site, business } = search;

  const getUsers = async () => {
    setIsLoading(true);
    try {
      let { results, count } = await api.get(endpoint.all_users, {
        params: {
          limit,
          business: business ? [business] : "",
          site,
          page,
          searchText,
          sortBy: "createdAt",
          orderBy: "DESC",
          role: role !== "all" ? role : null,
        },
      });
      results = results.map((item, idx) => {
        item.sl = (page - 1) * limit + idx + 1;
        return item;
      });
      setUsers(results);
      setTotal(count || 0);
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }

    setIsLoading(false);
  };

  const onConfirmActiveDeactiveUser = async (row) => {
    const data = {
      isActive: !row.isActive,
    };
    Swal.fire({
      title: "Are you sure?",
      text: "You want to change user status?",
      dangerMode: true,
      showCancelButton: true,
      confirmButtonText: "Confirm",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .put(`user/` + row._id, data, {
            withCredentials: true,
          })
          .then(() => {
            getUsers();
            Swal.fire({
              title: "Done!",
              text: "User Status Successfully Changed.",
              icon: "success",
              timer: 2000,
              button: false,
            });
          })
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.log(err);
          });
      }
    });
  };

  const deleteUser = async (userId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this user?",
      //icon: "warning",
      dangerMode: true,
      showCancelButton: true,
      confirmButtonText: "Confirm",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .delete(`user/` + userId, {
            withCredentials: true,
          })
          .then(() => {
            getUsers();
            Swal.fire({
              title: "Done!",
              text: "User Deleted Successfully",
              icon: "success",
              timer: 2000,
              button: false,
            });
          })
          .catch(() =>
            Swal.fire({
              title: "",
              text: "User Deleted Failed",
              icon: "error",
              timer: 2000,
              button: false,
            }),
          );
      }
    });
  };

  const getBusiness = async () => {
    setBusinessLoading(true);
    try {
      const data = await api.get(endpoint.business);
      setBusinessList(data?.data);
      if (Array.isArray(data?.data) && data.data.length === 1) {
        setSearch({ ...search, business: data.data[0]._id });
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log("business list", error);
    }
    setBusinessLoading(false);
  };

  const getSiteLocations = async () => {
    setSiteLoading(true);
    try {
      const data = await api.get(`/site-location/my-site`, {
        params: { business, limit: 100000 },
      });
      setSiteList(data);
    } catch {
      setSiteList([]);
    }
    setSiteLoading(false);
  };

  useEffect(() => {
    getUsers();
  }, [page, limit, role, searchText]);

  useEffect(() => {
    getSiteLocations();
  }, [business]);

  useEffect(() => {
    document.title = `${appConfig.name} - Users`;
    getBusiness();
  }, []);

  const columns = [
    {
      title: "No.",
      render: (_, { sl }) => sl,
      width: "40px",
      align: "center",
    },
    {
      title: "User Name",
      render: (_, row) => (
        <div className="d-flex my-2 align-items-center">
          <p className="m-0">{row?.name || "--"}</p>
        </div>
      ),
    },

    {
      title: "Login ID",
      render: (_, row) => row?.loginId || "",
    },
    {
      title: "User Type",
      render: (_, row) => <div className="text-capitalize">{getFormatedUserType(row.role)}</div>,
      align: "center",
    },
    {
      title: "Date Created",
      render: (_, row) => moment(row.createdAt).format("DD/MM/YYYY"),
      align: "center",
    },
    {
      title: "Last Login",
      render: (_, row) => (
        <div>
          {row?.lastLogin ? (
            <div>
              {moment(row.lastLogin).format("DD/MM/YYYY")}
              <br />
              {moment(row.lastLogin).format("hh:mm:ss A")}
            </div>
          ) : (
            "--"
          )}
        </div>
      ),
      align: "center",
    },
    {
      title: "Status",
      render: (_, row) => (
        <div>
          {row.isActive ? (
            <span className="badge text-bg-success">Active</span>
          ) : (
            <span className="badge text-bg-danger">Deactivated</span>
          )}
        </div>
      ),
      align: "center",
    },
    {
      title: "Action",
      align: "center",
      width: "200px",
      render: (_, row) => (
        <div>
          <Link to={`/users/details/${row._id}?role=${role || "all"}`} className="btn btn-az-primary me-1">
            <FiEye title="View Profile" />
          </Link>
          <Link to={`/users/edit/${row._id}?role=${role || "all"}`} className="btn btn-edit me-1">
            <FiEdit title="Edit Profile" />
          </Link>
          {row.isActive ? (
            <button className="btn btn-warning  me-1" onClick={() => onConfirmActiveDeactiveUser(row)}>
              <FiUserX title="Inactive Account" />
            </button>
          ) : (
            <button className="btn btn-success  me-1" onClick={() => onConfirmActiveDeactiveUser(row)}>
              <FiUserCheck title="Active Account" />
            </button>
          )}
          <button className="btn btn-danger" onClick={() => deleteUser(row._id)}>
            <FiTrash title="Delete" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="card p-3 m-md-3">
      <div className="d-flex flex-column flex-md-row g-3 align-items-md-center mb-3 justify-content-between">
        <div>
          <label className="az-content-label">Manage Users</label>
          <span className="d-block py-2">{getHeader(role).title}</span>
        </div>
        <div className="ms-md-auto p-0">
          {getHeader(role).title !== "All Users" && (
            <Link className="text-capitalize" to={`/users/create?role=${role || "public"}`}>
              <Button icon={<FaPlus />} type="primary">
                {getHeader(role).btnText || "Add user"}
              </Button>
            </Link>
          )}
        </div>
      </div>

      <hr className="mt-0" />

      <div className="row mb-2 g-3">
        <div className="col-sm-3">
          <Select
            style={{ width: "100%" }}
            size="large"
            id="business"
            name="business"
            loading={businessLoading}
            allowClear
            maxTagCount={1}
            value={business}
            onChange={(value) => setSearch((prev) => ({ ...prev, business: value, site: null }))}
            showSearch={true}
            filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
            placeholder="All Businesses"
            disabled={businessList?.length === 1}
            options={[...businessList.map((item) => ({ label: `${item.name} (${item.id})`, value: item._id }))]}
            required
          />
        </div>
        <div className="col-sm-3">
          <Select
            style={{ width: "100%" }}
            size="large"
            id="site"
            name="site"
            allowClear
            loading={siteLoading}
            value={site}
            onChange={(value) => setSearch((prev) => ({ ...prev, site: value }))}
            showSearch={true}
            filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
            placeholder="All Site Locations"
            options={[
              ...siteList.map((item) => ({
                label: `${item.name} ${item?.id ? "(" + item.id + ")" : ""} `,
                value: item._id,
              })),
            ]}
            required
          />
        </div>
        <div className="col-sm-3">
          <button
            className="btn bg-secondary-subtle text-black"
            onClick={() => {
              setPage(1);
              getUsers();
            }}
          >
            <FaSearch />
          </button>
        </div>
        <div className="col-sm-1"></div>
        <div className="col-sm-2">
          <Input
            type="search"
            size="large"
            value={searchText}
            onChange={(e) => {
              setPage(1);
              setSearchText(e.target.value);
            }}
            placeholder="Search"
            className="me-2"
            aria-label="Search"
          />
        </div>
      </div>

      <Spin spinning={isLoading}>
        <DataTable
          dataSource={users}
          columns={columns}
          rowKey={(record) => record.id}
          pagination={{
            defaultPageSize: limit,
            current: page,
            total,
            showSizeChanger: true,
            onShowSizeChange: (_, pageSize) => {
              setLimit(pageSize);
            },
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
            pageSizeOptions: [10, 20, 50],
            responsive: true,
          }}
          onChange={(pagination) => {
            setPage(pagination.current);
          }}
        />
      </Spin>
    </div>
  );
};

export default UserList;
